import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ChevronDown, ChevronRight, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { useCart } from "@/context/CartContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PartnerNudge } from "@/components/PartnerNudge";
import { BenefitBar } from "@/components/BenefitBar";

const money = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const SORTS = [["featured", "Featured"], ["newest", "Newest"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"]];

function ProductCard({ p }) {
  const [img, setImg] = useState(p.primary_image);
  return (
    <Link to={`/shop/${p.slug}`} className="group text-left" data-testid={`shop-card-${p.id}`}
      onMouseLeave={() => setImg(p.primary_image)}>
      <div className="relative aspect-square overflow-hidden bg-bone">
        {p.badges?.[0] && <span className="absolute top-2 left-2 z-10 bg-forest text-bone text-[10px] uppercase tracking-wide px-2 py-0.5">{p.badges[0]}</span>}
        {img && <img src={img} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[600ms]" />}
      </div>
      <h3 className="font-serif text-[15px] text-charcoal mt-3 leading-snug">{p.title}</h3>
      <p className="text-charcoal font-semibold text-sm mt-0.5">
        {p.sale_price ? <><span className="text-blaze">{money(p.sale_price)}</span> <span className="line-through text-charcoal/40 text-xs">{money(p.price)}</span></> : money(p.effective_price)}
      </p>
      {p.colors?.length > 0 && (
        <div className="flex gap-1.5 mt-2" onClick={(e) => e.preventDefault()}>
          {p.colors.slice(0, 6).map((c) => (
            <button key={c.name} title={c.name} onMouseEnter={() => c.image && setImg(c.image)}
              className="w-4 h-4 rounded-full border border-charcoal/20" style={{ background: c.hex }} />
          ))}
        </div>
      )}
      {p.sizes?.length > 0 && <p className="text-[11px] text-charcoal/45 mt-1.5 uppercase tracking-wide">{p.sizes.join("  ")}</p>}
    </Link>
  );
}

function Collapse({ title, children, open: o = true }) {
  const [open, setOpen] = useState(o);
  return (
    <div className="border-b border-charcoal/10 py-3">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full hr-overline text-charcoal">
        {title} <ChevronDown size={15} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-3 space-y-1.5">{children}</div>}
    </div>
  );
}

export default function Shop() {
  const { goods } = useSiteContent();
  const { count, setOpen } = useCart();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [price, setPrice] = useState({ min: "", max: "" });
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);
  const [mobileFilter, setMobileFilter] = useState(false);

  useEffect(() => { api.shopCategories().then(setCats).catch(() => setCats([])); }, []);
  useEffect(() => {
    setData(null);
    api.shopProducts({ category: cat, sizes: sizes.join(",") || undefined, colors: colors.join(",") || undefined,
      price_min: price.min || undefined, price_max: price.max || undefined, sort, page, limit: 16 })
      .then(setData).catch(() => setData({ total: 0, products: [] }));
  }, [cat, sizes, colors, price, sort, page]);

  const products = data?.products || [];
  const total = data?.total || 0;
  const allSizes = useMemo(() => [...new Set(products.flatMap((p) => p.sizes || []))], [products]);
  const allColors = useMemo(() => {
    const m = {}; products.forEach((p) => (p.colors || []).forEach((c) => { m[c.name] = c.hex; })); return m;
  }, [products]);
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const heroImg = goods?.hero_image || products[0]?.homepage_image || products[0]?.primary_image;

  const Sidebar = () => (
    <aside className="w-full lg:w-60 shrink-0" data-testid="shop-filters">
      <p className="hr-overline text-charcoal mb-2">Filter by</p>
      <Collapse title="Category">
        {[{ id: "all", name: "All Products" }, ...cats].map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-sm text-charcoal/70 cursor-pointer">
            <input type="radio" name="cat" checked={cat === c.id} onChange={() => { setCat(c.id); setPage(1); }} className="accent-[#8A5A44]" data-testid={`filter-cat-${c.id}`} /> {c.name}
          </label>
        ))}
      </Collapse>
      {allSizes.length > 0 && <Collapse title="Size">
        <div className="flex flex-wrap gap-1.5">
          {allSizes.map((s) => <button key={s} onClick={() => { toggle(sizes, setSizes, s); setPage(1); }} className={`text-xs border px-2 py-1 ${sizes.includes(s) ? "bg-saddle text-white border-saddle" : "border-charcoal/20 text-charcoal/60"}`}>{s}</button>)}
        </div>
      </Collapse>}
      {Object.keys(allColors).length > 0 && <Collapse title="Color">
        <div className="flex flex-wrap gap-2">
          {Object.entries(allColors).map(([n, hex]) => <button key={n} title={n} onClick={() => { toggle(colors, setColors, n); setPage(1); }} className={`w-6 h-6 rounded-full border-2 ${colors.includes(n) ? "border-saddle" : "border-charcoal/15"}`} style={{ background: hex }} />)}
        </div>
      </Collapse>}
      <Collapse title="Price">
        <div className="flex items-center gap-2">
          <input type="number" placeholder="$ Min" value={price.min} onChange={(e) => { setPrice({ ...price, min: e.target.value }); setPage(1); }} className="w-full border border-charcoal/20 px-2 py-1 text-sm" />
          <span>–</span>
          <input type="number" placeholder="$ Max" value={price.max} onChange={(e) => { setPrice({ ...price, max: e.target.value }); setPage(1); }} className="w-full border border-charcoal/20 px-2 py-1 text-sm" />
        </div>
      </Collapse>
    </aside>
  );

  return (
    <div className="bg-paper min-h-screen" data-testid="shop-page">
      <SiteHeader />
      {/* Hero */}
      <section className="relative bg-forest text-bone overflow-hidden" data-testid="shop-hero">
        {heroImg && <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 py-20 lg:py-28">
          <p className="hr-overline text-blaze">{goods?.eyebrow || "HOMERANGE GOODS"}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-3 max-w-xl">{goods?.headline || "Made for wherever you call home."}</h1>
          <p className="text-bone/75 mt-5 max-w-md">{goods?.description}</p>
        </div>
      </section>

      {/* Category nav + cart */}
      <div className="border-b border-charcoal/10 sticky top-0 bg-paper/95 backdrop-blur z-30">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 flex items-center justify-between gap-4">
          <div className="flex gap-5 sm:gap-7 overflow-x-auto py-4" data-testid="shop-catnav">
            {[{ id: "all", name: "All Products" }, ...cats].map((c) => (
              <button key={c.id} onClick={() => { setCat(c.id); setPage(1); }} className={`whitespace-nowrap text-sm pb-1 border-b-2 transition-colors ${cat === c.id ? "border-blaze text-charcoal font-medium" : "border-transparent text-charcoal/55 hover:text-charcoal"}`} data-testid={`catnav-${c.id}`}>{c.name}</button>
            ))}
          </div>
          <button onClick={() => setOpen(true)} className="shrink-0 flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-blaze" data-testid="shop-viewcart">
            <ShoppingBag size={16} /> View Cart ({count})
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10 flex gap-10">
        <div className="hidden lg:block"><Sidebar /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-charcoal/55" data-testid="shop-count">{total === 0 ? "No products" : `Showing 1–${Math.min(16, total)} of ${total} products`}</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileFilter(true)} className="lg:hidden inline-flex items-center gap-1 text-sm text-charcoal/70"><SlidersHorizontal size={15} /> Filter</button>
              <label className="text-sm text-charcoal/60 flex items-center gap-1.5">Sort:
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent font-medium text-charcoal outline-none" data-testid="shop-sort">
                  {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
          </div>
          {data === null ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square bg-bone animate-pulse" />)}</div>
          ) : products.length === 0 ? (
            <p className="text-charcoal/55 py-16 text-center">No products match these filters yet.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-9" data-testid="shop-grid">
              {products.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>

      {mobileFilter && (
        <div className="fixed inset-0 z-[80] lg:hidden" onClick={() => setMobileFilter(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute left-0 top-0 h-full w-72 bg-paper p-5 overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><span className="font-serif text-xl">Filters</span><button onClick={() => setMobileFilter(false)}><ChevronRight size={20} /></button></div>
            <Sidebar />
          </div>
        </div>
      )}

      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-4">
        <PartnerNudge preferType="brand" label="Featured gear partner" />
      </div>
      <BenefitBar />
      <Footer />
    </div>
  );
}
